import {Alert} from 'src/design/atoms/Alert';
import {Group} from 'src/design/atoms/Group';
import {Progress} from 'src/design/atoms/Progress';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconDownload} from '@tabler/icons-react';
import React, {useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {discardOfflineSession} from 'src/offline/offlineSessionManager';
import type {DownloadProgress} from 'src/app/inspections/offline/inspectionBundle';
import {downloadInspectionForOffline} from 'src/app/inspections/offline/inspectionBundle';
import {useInspectionOfflineDownload} from 'src/app/inspections/offline/useInspectionOfflineSession';
import {useFetchInspectionOfflineBundleMutation} from 'src/redux/queries/inspection';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface PrepareInspectionCardProps {
  inspectionId: number;
}

// Downloads the version-pinned bundle + photos so the inspection can be run
// disconnected; only a fully completed download is offline-ready.
export const PrepareInspectionCard: React.FC<PrepareInspectionCardProps> = ({
  inspectionId,
}) => {
  const {isDownloaded, isDownloading, session} =
    useInspectionOfflineDownload(inspectionId);
  const [progress, setProgress] = useState<DownloadProgress | undefined>();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [fetchBundle] = useFetchInspectionOfflineBundleMutation();

  const run = async () => {
    setRunning(true);
    setError(undefined);
    try {
      await downloadInspectionForOffline(
        inspectionId,
        id => fetchBundle(id).unwrap(),
        setProgress
      );
    } catch (caught) {
      setError(parseErrorMessage(caught));
    } finally {
      setRunning(false);
    }
  };

  const discard = async () => {
    if (session) await discardOfflineSession(session.id);
    setProgress(undefined);
    setError(undefined);
  };

  const busy = running || (isDownloading && !error);

  return (
    <SectionCard title="Download for offline" icon={<IconDownload size={16} />}>
      <Stack gap="md" p="md">
        {isDownloaded ? (
          <InfoAlert variant="success">
            Ready — you can disconnect and inspect offline. Your work syncs when
            you reconnect.
          </InfoAlert>
        ) : (
          <Text size="sm" c="dimmed">
            Download this inspection and its photos to your device so you can
            work without a connection.
          </Text>
        )}

        {progress?.persistenceDenied && (
          <Alert color="yellow">
            Your browser may clear this download if storage runs low — keep this
            tab open until you sync.
          </Alert>
        )}

        {busy && progress && (
          <Stack gap={4}>
            <Text size="sm">
              {progress.phase === 'bundle'
                ? 'Downloading inspection data…'
                : progress.phase === 'photos'
                ? `Downloading photos (${progress.photosDone}/${progress.photosTotal})`
                : 'Finishing…'}
            </Text>
            <Progress
              value={
                progress.photosTotal > 0
                  ? ((progress.photosDone + progress.photosFailed) /
                      progress.photosTotal) *
                    100
                  : progress.phase === 'bundle'
                  ? 10
                  : 100
              }
              animated={progress.phase !== 'ready'}
            />
          </Stack>
        )}

        {progress != null && progress.photosFailed > 0 && (
          <Alert color="yellow">
            {progress.photosFailed} photo
            {progress.photosFailed === 1 ? '' : 's'} couldn&apos;t be downloaded
            and won&apos;t be available offline.
          </Alert>
        )}

        {error && <Alert color="red">{error}</Alert>}

        {!isDownloaded && (
          <Group gap="sm">
            <Button
              color="yellow"
              loading={busy}
              leftSection={<IconDownload size={16} />}
              onClick={run}>
              {error || isDownloading
                ? 'Retry download'
                : 'Download for offline'}
            </Button>
            {(isDownloading || error) && !running && (
              <Button variant="subtle" color="black" onClick={discard}>
                Discard
              </Button>
            )}
          </Group>
        )}
      </Stack>
    </SectionCard>
  );
};
